'use client';
/* Built In Imports */
import { usePathname, useSearchParams } from 'next/navigation';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

/* External Imports */
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';
import * as Yup from 'yup';

/* Internal Imports */
/* Components */
import config from '@config';
import { CountriesList } from '@constants/index';

/* Services */
import { addSubscription } from '@Services/newsletterService';

const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), { ssr: false });

/**
 * Renders the Subscribe Box component
 *
 * @param {object} newsletters
 * @param {string} type
 * @param {string} inputBackground
 * @param {string} submitText
 * @param {boolean} hideSpamText Want to show Spam text or not
 * @param type
 * @param inputBackground
 * @param submitText
 * @param hideSpamObj
 * @param hideSpamText
 * @returns {ReactElement} Subscribe Box component.
 */

const SubscribeBox = ({ sectionContent, lang, response }) => {
  const router = usePathname();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const finalRef = useRef(null);
  const [messageTemp, setMessageTemp] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  // const fontSize = { base: '2xs', md: 'xs' };

  const initialVal = {
    name: '',
    email: '',
    country: '',
    newsletters: {},
    source: `${config.BASE_PATH}${router}`,
  };

  initialVal.newsletters[sectionContent?.newsletterIdentifier] =
    sectionContent?.newsletterIdentifier;

  const ValidationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^([a-zA-Z ])+$/u, {
        message: 'Name is invalid',
      })
      .max(100, 'Name is invalid')
      .required('Name is required'),
    email: Yup.string()
      .required('Email is required')
      .matches(/^[a-zA-Z]+[a-zA-Z0-9._]+@[a-zA-Z]+(\.[a-zA-Z]{2,15})+$/, {
        message: 'Email is invalid',
      }),
    country: Yup.string().required('Country is required'),
    recaptcha: Yup.string().required('Cannot go past captcha'),
  });

  // updated onsubmit according to search params
  const onSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      setLoading(true);
      const formValues = { ...values, language: lang, skip_welcome_mail: true };
      delete formValues.recaptcha;

      setSubmitting(true);
      const formResponse = await addSubscription(formValues);
      setSubmitting(false);
      resetForm();

      if (typeof formResponse === 'string') {
        setMessageTemp(
          `<div>Sorry, something went wrong. We are unable to process your request right now.</div>`
        );
      } else if (
        formResponse[0]?.status_code >= 101 &&
        formResponse[0]?.status_code <= 106
      ) {
        setMessageTemp(formResponse[0]?.message_template);
        response(formResponse);
      } else {
        setMessageTemp(
          `<div>Sorry, something went wrong. We are unable to process your request right now.</div>`
        );
      }

      onOpen();
      setLoading(false);

      const popupUrl = searchParams.has('popup')
        ? `${pathname}?popup=1`
        : `${pathname}?popup=1`;

      window.history.pushState(window.history.state, '', popupUrl);
    },
    [lang, pathname, searchParams, sectionContent]
  );

  useEffect(() => {
    if (!isOpen && searchParams.has('popup')) {
      const updatedUrl = `${pathname}${searchParams
        .toString()
        .replace(/&?popup=1/, '')}`;
      window.history.pushState(window.history.state, '', updatedUrl);
    }

    recaptchaRef.current?.reset();
  }, [isOpen, searchParams, pathname]);

  useEffect(() => {
    window.onpopstate = () => {
      if (searchParams.has('popup')) {
        onClose();
      }
    };
  }, [onClose, searchParams]);

  return (
    <>
      <Flex
        justifyContent="center"
        alignItems="center"
        width={{ base: '100%', md: '100%' }}
        mx={{ md: 'auto' }}
        pb="50px"
        // px={{ base: '5%', md: '0' }}
      >
        <Formik
          initialValues={initialVal}
          validationSchema={ValidationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {props => (
            <Form
              onSubmit={props.handleSubmit}
              style={{
                rowGap: '21px',
                columnGap: '10px',
                boxSizing: 'border-box',
                placeContent: 'center',
                maxWidth: '100%',
                width: '100%',
              }}
              fontFamily="var(--FedraSansStd-book)"
            >
              <Flex
                flexDirection={{ base: 'column', md: 'column' }}
                mx={{ base: 'auto' }}
                alignItems={{ base: 'center', md: 'flex-start' }}
                justifyContent="flex-start"
                maxW={{ md: 480, lg: 680, xl: '470' }}
                w="100%"
                flexWrap="wrap"
                fontFamily="var(--FedraSansStd-book)"
                gridGap={{ base: '0', sm: '10px', md: '1rem' }}
              >
                <Field name="name">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.name && form.touched.name}
                      display="flex"
                      flexDir="column"
                      // fontFamily="var(--font-zilla-Slab)"
                      w="100%"
                      mb={{ base: '16px', md: '10px' }}
                    >
                      <Input
                        // fontFamily="var(--font-zilla-Slab)"
                        color="#F1EDE9"
                        {...field}
                        id="name"
                        w="100%"
                        height={{ base: '45px', md: '50px', '2xl': '49px' }}
                        placeholder={
                          sectionContent?.content?.json?.name || 'Full Name'
                        }
                        onChange={props.handleChange}
                        fontStyle="italic"
                        padding="12px 16px 12px 16px"
                        required
                        fontSize="16px"
                        _placeholder={{
                          color: 'rgba(241, 237, 233, 0.7)',

                          fontSize: '16px',
                        }}
                        border="transparent"
                        background="rgba(241, 237, 233, 0.1)"
                        rounded="none"
                        fontFamily="var(--FedraSansStd-book)"
                      />
                      <FormErrorMessage
                        justifyContent="left"
                        // position="absolute"
                        // margin="0"
                        // bottom="-25px"
                        fontFamily="var(--FedraSansStd-book)"
                        // left="5px"
                      >
                        {form.errors.name}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.email && form.touched.email}
                      w="100%"
                      mb={{ base: '16px', md: '10px' }}
                    >
                      <Input
                        // fontFamily="var(--font-zilla-Slab)"

                        background="rgba(241, 237, 233, 0.1)"
                        // border="0.3px solid #000000"
                        {...field}
                        id="email"
                        required
                        fontFamily="var(--FedraSansStd-book)"
                        height={{ base: '45px', md: '50px', '2xl': '49px' }}
                        w="100%"
                        placeholder={
                          sectionContent?.content?.json?.email || 'Email'
                        }
                        borderColor="transparent"
                        onChange={props.handleChange}
                        padding="12px 16px 12px 16px"
                        fontStyle="italic"
                        fontSize="16px"
                        color="rgba(241, 237, 233, 0.7)"
                        _placeholder={{
                          color: 'rgba(241, 237, 233, 0.7)',

                          fontSize: '16px',
                        }}
                        rounded="none"
                      />
                      <FormErrorMessage
                        justifyContent="left"
                        // position="absolute"
                        // margin="0"
                        // bottom="-25px"
                        // left="5px"
                      >
                        {form.errors.email}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="country">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.country && form.touched.country}
                      w="100%"
                      mb={{ base: '16px', md: '10px' }}
                    >
                      <Select
                        iconSize="25px"
                        key="country"
                        iconColor="#fff"
                        borderColor="transparent"
                        {...field}
                        id="country"
                        isRequired={true}
                        fontSize="16px"
                        _placeholder={{
                          fontWeight: '350',
                          // color: '#F1EDE9',
                          // color: 'rgba(241, 237, 233, 0.1)',
                          // opacity: 0.1,
                          fontSize: '16px',
                        }}
                        fontFamily="var(--FedraSansStd-book)"
                        fontStyle="italic"
                        background="rgba(241, 237, 233, 0.1)"
                        // width={{ base: '100%', md: '210px' }}
                        height={{ base: '45px', md: '50px', '2xl': '49px' }}
                        // w={{
                        //   sm: '210px',
                        //   base: '100%',
                        // }}
                        rounded="none"
                        placeholder={
                          sectionContent?.content?.json?.country || 'Country'
                        }
                        icon={
                          <Icon
                            width="24px"
                            height="24px"
                            viewBox="0 0 24px 24px"
                            fill="none"
                          >
                            <path
                              d="M16 10L12 14L8 10"
                              stroke="#F1EDE9"
                              stroke-width="2.66667"
                              strokeLinecap="square"
                            />
                          </Icon>
                        }
                        // margin="4.5px"
                        color="rgba(241, 237, 233, 0.7)"
                      >
                        {CountriesList?.map(country => {
                          return (
                            <option
                              style={{ color: '#64748B' }}
                              key={nanoid()}
                              value={country.value}
                            >
                              {country.name}
                            </option>
                          );
                        })}
                      </Select>
                      <FormErrorMessage
                      // position="absolute"
                      // margin="0"
                      // bottom="-25px"
                      // left="5px"
                      >
                        {form.errors.country}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Flex>

              <Flex
                flexDirection={{ base: 'column', md: 'column' }}
                maxW={{ base: 'auto', xl: '386px' }}
                mx={{ base: '0', xl: 'auto' }}
                // maxH={{ base: 'auto', md: '45px' }}
                placeItems="center"
                mt={{ base: '10px', sm: '0px', md: '1rem', lg: '0' }}
                gridAutoFlow={{ base: 'row', md: 'column' }}
                alignContent="center"
                justifyContent="center"
              >
                <Field name="recaptcha">
                  {({ form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.recaptcha && form.touched.recaptcha
                      }
                      m={{ base: '0', md: '0 0px' }}
                      textAlign="center"
                      // height="50px"
                      overflow={{ base: 'hidden', lg: 'visible' }}
                      maxW={{ base: '270px', lg: '290px' }}
                      w="100%"
                      maxH={20}
                    >
                      <Box
                        mx={{ base: 'auto', md: '0' }}
                        mt={{ base: '0', md: '0', lg: '4px' }}
                        style={{
                          transform: 'scale(0.8)',
                          transformOrigin: '88px 50px',
                        }}
                      >
                        {config.RECAPTCHA_KEY && (
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={config.RECAPTCHA_KEY}
                            onChange={success => {
                              success === null
                                ? recaptchaRef.current.reset()
                                : props.setFieldValue('recaptcha', success);
                            }}
                            onErrored={() => {
                              props.setFieldValue('recaptcha', '');
                              recaptchaRef.current.reset();
                            }}
                            size="normal"
                            // style={{ background: '#222' }}
                          />
                        )}
                      </Box>
                      <FormErrorMessage
                        justifyContent="left"
                        fontFamily="var(--FedraSansStd-book)"
                      >
                        {form.errors.recaptcha}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Box mt={{ base: '10px', md: '0' }} width={{ base: '100%' }}>
                  <Button
                    type="submit"
                    variant="primary"
                    bg="#60ADFF"
                    rounded="none"
                    w={{ base: '100%', md: '157px' }}
                    fontWeight={500}
                    padding={{ base: '12px 32px 12px 32px' }}
                    color={'#010509'}
                    h={{ base: '49px', '2xl': '50px' }}
                    fontFamily="var(--FedraSansStd-medium)"
                    mt={{ base: '0', md: '16px', lg: '32px' }}
                    margin={{ base: '10px auto', md: '10px 4.5px' }}
                  >
                    <Flex justifyContent="center" alignItems="center">
                      <Text pr={'5px'} fontSize="16px" letterSpacing="2%">
                        {loading
                          ? 'Submitting..'
                          : sectionContent?.content?.json?.submitButton}
                      </Text>
                    </Flex>
                  </Button>
                </Box>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>

      {messageTemp && (
        <Box
          maxW={messageTemp ? { base: '90%', md: '476px' } : 'auto'}
          m="20px auto"
        >
          <Modal
            finalFocusRef={finalRef}
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
            size={!messageTemp ? 'xl' : '4xl'}
            blockScrollOnMount={true}
            closeOnOverlayClick={false}
          >
            <ModalOverlay />
            <ModalContent p="30px" bg="#F8F3EE" rounded="0">
              <ModalCloseButton color="#9F9F9F" mt="1" />
              <ModalBody fontFamily="var(--FedraSansStd-book)">
                {parser(messageTemp || '')}
              </ModalBody>
            </ModalContent>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default memo(SubscribeBox);
